import React, { useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Drawer,
  useMediaQuery,
  FormControlLabel,
  Switch,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import getApiBase from "../../components/getApiBase";
import logo from "../../assets/UpdatedMainLogo.png";
import {
  getAdminV2Theme,
  readStoredRailMode,
  writeStoredRailMode,
} from "./theme";
import {
  PUBLIC_INFO_EMAIL,
  publicInfoMailtoHref,
} from "../../constants/publicContact";

/** Top-level groups only — no nested children (hubs open on the page). */
const FALLBACK_NAV = [
  { key: "overview", label: "Overview", path: "/admin/v2", live: true },
  { key: "sales", label: "Sales & Marketing", path: "/admin/v2/sales", live: false },
  { key: "operations", label: "Operations", path: "/admin/v2/operations", live: true },
  { key: "finance", label: "Business & Finance", path: "/admin/v2/finance", live: true },
  { key: "analytics", label: "Analytics & Traffic", path: "/admin/v2/analytics", live: false },
  { key: "users", label: "Users & Access", path: "/admin/v2/users", live: true },
  { key: "security", label: "Security & Compliance", path: "/admin/v2/security", live: false },
  { key: "dev", label: "Development & Health", path: "/admin/v2/dev", live: true },
];

function envBadge(t) {
  const hostname = window.location.hostname;
  if (hostname.includes("git-preprod") || hostname.includes("preprod")) {
    return { label: "Staging", color: t.colors.accent };
  }
  if (hostname.includes("localhost")) {
    return { label: "Local", color: t.colors.accent };
  }
  if (hostname.includes("git-task") || hostname.includes("vercel.app")) {
    return { label: "Preview", color: t.colors.primary };
  }
  return { label: "Admin v2", color: t.colors.textMuted };
}

function NavList({ nav, isActive, onNavigate, t }) {
  return (
    <Box sx={{ p: 1, flex: 1, overflowY: "auto" }}>
      {nav.map((group) => {
        const active = isActive(group.path);
        return (
          <Box
            key={group.key}
            component={group.live ? Link : "div"}
            to={group.live ? group.path : undefined}
            onClick={() => {
              if (group.live && onNavigate) onNavigate();
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 1.5,
              py: 1.1,
              mb: 0.4,
              borderRadius: `${t.radius.sm}px`,
              textDecoration: "none",
              color: active ? t.colors.navActiveText : t.colors.navText,
              background: active ? t.colors.navActive : "transparent",
              borderLeft: active
                ? `3px solid ${t.colors.navActiveBar}`
                : "3px solid transparent",
              fontWeight: active ? 600 : 500,
              fontSize: 14,
              cursor: group.live ? "pointer" : "default",
              opacity: group.live ? 1 : 0.45,
              "&:hover": group.live
                ? { background: active ? t.colors.navActive : t.colors.navHover }
                : {},
            }}
          >
            <span>{group.label}</span>
            {!group.live && (
              <Chip
                label="Soon"
                size="small"
                sx={{
                  height: 18,
                  fontSize: 10,
                  background: t.colors.navSoonBg,
                  color: t.colors.navMuted,
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
}

export default function AdminV2Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width:900px)");
  const [navOpen, setNavOpen] = useState(false);
  const [nav, setNav] = useState(FALLBACK_NAV);
  const [me, setMe] = useState(null);
  const [error, setError] = useState("");
  const [railMode, setRailMode] = useState(() =>
    typeof window !== "undefined" ? readStoredRailMode() : "light",
  );

  const t = useMemo(() => getAdminV2Theme(railMode), [railMode]);
  const badge = envBadge(t);
  const isDarkRail = t.railMode === "dark";

  const toggleRail = () => {
    const next = railMode === "dark" ? "light" : "dark";
    setRailMode(next);
    writeStoredRailMode(next);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [meRes, navRes] = await Promise.all([
          fetch(`${getApiBase()}/api/admin/v2/me`, { credentials: "include" }),
          fetch(`${getApiBase()}/api/admin/v2/nav`, { credentials: "include" }),
        ]);
        if (meRes.status === 401 || meRes.status === 403) {
          navigate("/admin");
          return;
        }
        if (meRes.ok) setMe(await meRes.json());
        if (navRes.ok) {
          const data = await navRes.json();
          if (data.groups?.length) {
            setNav(data.groups.map(({ children, ...rest }) => rest));
          }
        }
      } catch {
        setError("Failed to load admin session");
      }
    };
    load();
  }, [navigate]);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  const isActive = (path) =>
    path === "/admin/v2"
      ? location.pathname === "/admin/v2"
      : location.pathname === path || location.pathname.startsWith(`${path}/`);

  const rail = (
    <Box
      sx={{
        width: t.navWidth,
        height: "100%",
        background: t.colors.navBg,
        display: "flex",
        flexDirection: "column",
        color: t.colors.navText,
        borderRight: `1px solid ${t.colors.navBorder}`,
      }}
    >
      <Box sx={{ p: 2, borderBottom: `1px solid ${t.colors.navBorder}` }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
          <Box
            component={Link}
            to="/admin/v2"
            onClick={() => setNavOpen(false)}
            sx={{
              display: "block",
              flex: 1,
              minWidth: 0,
              textDecoration: "none",
              background: t.colors.navLogoPlate,
              borderRadius: `${t.radius.sm}px`,
              px: isDarkRail ? 1.25 : 0,
              py: isDarkRail ? 1 : 0.25,
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="Autoshops.com"
              sx={{
                display: "block",
                width: "100%",
                maxWidth: 200,
                height: "auto",
              }}
            />
          </Box>
          {isMobile && (
            <IconButton
              size="small"
              onClick={() => setNavOpen(false)}
              sx={{ color: t.colors.navMuted, mt: 0.5 }}
              aria-label="Close menu"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <Typography sx={{ color: t.colors.navMuted, fontSize: 12, mt: 1.25, px: 0.5 }}>
          Admin · v2
        </Typography>
      </Box>
      <NavList nav={nav} isActive={isActive} onNavigate={() => setNavOpen(false)} t={t} />
      <Box sx={{ p: 2, borderTop: `1px solid ${t.colors.navBorder}`, display: "flex", flexDirection: "column", gap: 1.25 }}>
        <FormControlLabel
          sx={{
            m: 0,
            px: 0.5,
            justifyContent: "space-between",
            width: "100%",
            ml: 0,
            mr: 0,
            "& .MuiFormControlLabel-label": {
              fontSize: 13,
              color: t.colors.navMuted,
              fontFamily: t.font.family,
            },
          }}
          labelPlacement="start"
          control={
            <Switch
              size="small"
              checked={isDarkRail}
              onChange={toggleRail}
              inputProps={{ "aria-label": "Dark menu rail" }}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": { color: t.colors.primary },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: t.colors.primary,
                },
              }}
            />
          }
          label={isDarkRail ? "Dark menu" : "Light menu"}
        />
        <Button
          fullWidth
          component="a"
          href={publicInfoMailtoHref()}
          variant="text"
          sx={{
            textTransform: "none",
            color: t.colors.navMuted,
            fontFamily: t.font.family,
            fontSize: 13,
            justifyContent: "flex-start",
            px: 0.5,
            "&:hover": { color: t.colors.primary, background: "transparent" },
          }}
        >
          Support · {PUBLIC_INFO_EMAIL}
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => navigate("/admin")}
          sx={{
            textTransform: "none",
            borderColor: t.colors.navButtonBorder,
            color: t.colors.navText,
            fontFamily: t.font.family,
            "&:hover": {
              borderColor: t.colors.primary,
              background: t.colors.primarySoft,
            },
          }}
        >
          Classic admin
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        background: t.colors.bg,
        fontFamily: t.font.family,
        color: t.colors.text,
      }}
    >
      {!isMobile && (
        <Box component="nav" sx={{ width: t.navWidth, flexShrink: 0 }}>
          {rail}
        </Box>
      )}

      {isMobile && (
        <Drawer
          open={navOpen}
          onClose={() => setNavOpen(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: t.navWidth,
              background: t.colors.navBg,
              borderRight: `1px solid ${t.colors.navBorder}`,
            },
          }}
        >
          {rail}
        </Drawer>
      )}

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <Box
          sx={{
            minHeight: 56,
            px: { xs: 1.5, sm: 3 },
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            background: t.colors.surface,
            borderBottom: `1px solid ${t.colors.border}`,
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            {isMobile && (
              <IconButton
                onClick={() => setNavOpen(true)}
                aria-label="Open menu"
                sx={{ color: t.colors.text }}
              >
                <MenuIcon />
              </IconButton>
            )}
            {isMobile && (
              <Box
                component={Link}
                to="/admin/v2"
                sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}
              >
                <Box
                  component="img"
                  src={logo}
                  alt="Autoshops.com"
                  sx={{ height: 28, width: "auto", maxWidth: 140 }}
                />
              </Box>
            )}
            <Chip
              label={badge.label}
              size="small"
              sx={{ background: badge.color, color: "#fff", fontWeight: 600 }}
            />
          </Box>
          <Typography
            sx={{
              fontSize: 13,
              color: t.colors.textMuted,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: { xs: 140, sm: 280 },
            }}
          >
            {me?.user?.email || "Admin"}
          </Typography>
        </Box>
        <Box sx={{ p: { xs: 2, sm: 3 }, flex: 1 }}>
          {error && (
            <Typography sx={{ color: t.colors.danger, mb: 2 }}>{error}</Typography>
          )}
          <Outlet context={{ me, railMode }} />
        </Box>
      </Box>
    </Box>
  );
}
